<script lang="ts">
  import {onDestroy} from 'svelte';
  import {Link, navigateTo, router} from 'yrv';
  import {marked} from 'marked';
  import JSZip from 'jszip';
  import {saveAs} from 'file-saver';
  import InlineSVG from 'svelte-inline-svg';
  import {getDownloadURL, getStorage, ref} from 'firebase/storage';

  import {
    Confidentiality,
    ProjectType,
    URLTypes,
    type ImageMedia
  } from '../../types/project';

  import {projects} from '../../stores/projects';
  import {auth, user} from '../../stores/user';
  import {Language, selectedLanguage} from '../../stores/language';

  import slides from '../../utils/slides';
  import {isValidDate} from '../../utils/date-utils';

  import {LIST, EDIT_PROJECT} from '../../constants/views';

  import editIcon from '../../assets/icons/pencil.svg';
  import slidesIcon from '../../assets/icons/images.svg';
  import downloadIcon from '../../assets/icons/download.svg';

  import MediaSlider from '../media-slider/MediaSlider.svelte';
  import NotFound404Page from '../404-page/404Page.svelte';
  import Chip from '../chip/Chip.svelte';
  import ConfidentialBadge from '../confidential-badge/ConfidentialBadge.svelte';
  import DraftBadge from '../draft-badge/DraftBadge.svelte';
  import LoadingDots from '../loader/LoadingDots.svelte';
  import Diamond from '../diamond/Diamond.svelte';
  import Button from '../button/Button.svelte';
  import LinkButton from '../link-button/LinkButton.svelte';
  import ProjectCard from '../project-card/ProjectCard.svelte';

  marked.setOptions({breaks: true});

  export let id = '';

  let isDiamondHovered = false;

  let isLoadingProject = true;
  let isSubscribedToUserStore = false;

  $: isEnglishSelected = $selectedLanguage === Language.EN;

  // Update project once when subscribed and when id changed
  const unsubscribeRouterStore = router.subscribe(async ({initial}) => {
    if (!initial && id) {
      isLoadingProject = true;
      await projects.fetchProject(id);
      isLoadingProject = false;
    }
  });

  // Update project on each user update
  const unsubscribeUserStore = user.subscribe(async _ => {
    if (id && isSubscribedToUserStore) {
      isLoadingProject = true;
      await projects.fetchProject(id);
      isLoadingProject = false;
    } else {
      isSubscribedToUserStore = true;
    }
  });

  onDestroy(() => {
    unsubscribeRouterStore();
    unsubscribeUserStore();
  });

  /** Export slides */
  let isGeneratingSlides = false;
  let slideUrl = '';
  let slideOpened = false;
  let slideError = false;

  const onSlideExportCompleted = () => {
    isGeneratingSlides = false;

    if (slideUrl && !slideOpened) {
      window.open(slideUrl, '_blank');
    }
  };

  const onSlideExportGotUrl = (url: string) => {
    slideUrl = url;
  };

  const onSlideExportError = () => {
    isGeneratingSlides = false;
    slideError = true;
  };

  const exportToGoogleSlides = async (isRetry?: boolean) => {
    if (project && (!isGeneratingSlides || isRetry)) {
      isGeneratingSlides = true;
      slideUrl = '';
      slideOpened = false;
      slideError = false;

      // Request access token if not available, before talking to the Google API
      if (!gapi.client.getToken()) {
        try {
          await auth.requestAccessToken();
        } catch (error) {
          console.error(error);
          onSlideExportError();
          return;
        }
      }

      slides.createPresentation(
        [{...project, id}],
        onSlideExportCompleted,
        onSlideExportGotUrl,
        (error: any) => {
          // Revoke API token and retry slides export once if failed with status 401 Unauthorized
          if (!isRetry) {
            const errorStatus = typeof error === 'object' && error.status;

            if (errorStatus === 401) {
              gapi.client.setToken(null);
              exportToGoogleSlides(true);
              return;
            }
          }

          onSlideExportError();
        }
      );
    }
  };

  $: project = $projects.get(id);

  $: title = isEnglishSelected
    ? project?.title_En || project?.title
    : project?.title;

  $: description = isEnglishSelected
    ? project?.description_En || project?.description
    : project?.description;

  $: relatedProjects = (project?.relatedProjects || [])
    .map(projectId => $projects.get(projectId))
    .filter(Boolean as any as <T>(x: T | undefined) => x is T);

  $: urlsByType = Object.values(URLTypes).reduce(
    (byType: Record<string, Array<string>> | null, type: URLTypes) => {
      const filteredUrls = ((project && project.urls) || [])
        .filter(url => url.type === type)
        .map(({url}) => url);
      return filteredUrls && filteredUrls.length
        ? {...(byType || {}), [type]: filteredUrls}
        : byType;
    },
    null
  );

  /** Download images */
  let isDownloadingImages = false;

  const downloadAllProjectImages = async () => {
    const currentProject = project;

    if (currentProject && currentProject.media && !isDownloadingImages) {
      isDownloadingImages = true;

      try {
        const projectMedia = currentProject.media;

        // Get all images from Firebase storage
        const downloadUrls = await Promise.all(
          projectMedia
            .filter<ImageMedia>(
              (media): media is ImageMedia => media.type === 'image'
            )
            .map(media => getDownloadURL(ref(getStorage(), media.url)))
        );
        const downloadedFiles = await Promise.all(
          downloadUrls.map(url => fetch(url).then(res => res.blob()))
        );

        // Create zip file with all images
        const zip = new JSZip();
        downloadedFiles.forEach((file, index) =>
          zip.file((projectMedia[index] as ImageMedia).name, file)
        );
        const content = await zip.generateAsync({type: 'blob'});

        // Download zip file
        saveAs(content, currentProject.title);

        isDownloadingImages = false;
      } catch (error) {
        console.error(error);
        isDownloadingImages = false;
      }
    }
  };
</script>

<style lang="postcss">
  .loadingContainer {
    display: flex;
    flex-grow: 1;
    align-self: center;
    align-items: center;
    justify-content: center;
    padding-bottom: 2vh;
  }

  .container {
    width: 100%;
    max-width: 67rem;
    margin: 0 auto;
  }

  @media (max-width: 639px) {
    .container {
      width: calc(100% + 1rem);
      margin: -0.5rem;
    }
  }

  @media (min-width: 768px) {
    .container {
      padding: 0 3.5rem;
    }
  }

  .projectContainer {
    width: 100%;
    overflow: hidden;
  }

  @media (min-width: 640px) {
    .projectContainer {
      border-radius: var(--ubilabs-border-radius-medium);
      box-shadow: 0 1px 2px rgba(var(--ubilabs-black-rgb), 0.14),
        0 1px 2px rgba(var(--ubilabs-black-rgb), 0.12);
    }
  }

  .projectContainer header {
    position: relative;
    display: grid;
    gap: 0.25rem 0.5rem;
    padding: 0.25rem 0.5rem;
  }

  @media (min-width: 640px) {
    .projectContainer header {
      grid-template-columns: 1fr auto;
      padding: 0.5rem 1rem;
    }
  }

  .projectContainer header:first-child {
    padding-top: 0.5rem;
  }

  @media (min-width: 640px) {
    .projectContainer header:first-child {
      padding-top: 1rem;
    }
  }

  .projectContainer header h1 {
    margin: 0;
  }

  .projectContainer header .badges {
    margin: 0.5rem 0;
  }

  @media (min-width: 640px) {
    .projectContainer header .badges {
      margin: 0.25rem 0;
    }
  }

  .missingData {
    color: var(--ubilabs-gray-dark-1);
  }

  .period {
    grid-row: 2;
    color: var(--ubilabs-gray-dark-2);
  }

  .details {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.5rem;
  }

  @media (min-width: 640px) {
    .details {
      padding: 1rem;
    }
  }

  @media (min-width: 768px) {
    .details {
      display: grid;
      grid-template-columns: 20rem 1fr;
    }
  }

  .details dd {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .details dd a {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .details dt.chips,
  .details dd.chips .missingData {
    display: inline-block;
    padding-top: 0.375rem;
  }

  .details dd.chips {
    overflow: visible;
  }

  .details dd.chips > div {
    margin: 0 calc(var(--ubilabs-chip-margin-horizontal) * -2);
  }

  .overview {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .diamondWrapper {
    max-width: 320px;
    margin: 0 auto;
  }

  .projectActions {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  @media (max-width: 639px) {
    .projectActions {
      flex-direction: column;
      padding: 0 0.5rem 1rem;
    }

    .projectActions :global(button),
    .projectActions > :global(a) {
      justify-content: center;
      width: 100%;
    }
  }

  @media (min-width: 640px) {
    .projectActions > :global(a) {
      margin-left: auto;
    }
  }

  .exportWrapper {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  @media (min-width: 640px) {
    .exportWrapper {
      min-height: 4rem;
    }
  }

  @media (max-width: 639px) {
    .exportWrapper {
      align-items: center;
    }

    .exportWrapper > a,
    .exportWrapper > .errorMessage {
      margin-bottom: 0.25rem;
    }
  }

  .errorMessage {
    display: block;
    color: var(--ubilabs-error-color);
  }

  .relatedContent {
    margin-top: 2rem;
  }

  @media (max-width: 639px) {
    .relatedContent {
      padding: 0 0.5rem;
    }
  }

  .relatedProjects {
    overflow: auto;
    padding: 0 1px 1rem;
  }

  .relatedProjects > div {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max(200px, calc((100% - 3rem) / 3.75));
    gap: 1rem;
  }

  @media (max-width: 899px) {
    .relatedProjects > div {
      grid-auto-columns: max(200px, calc((100% - 3rem) / 2.75));
    }
  }

  @media (max-width: 639px) {
    .relatedProjects > div {
      grid-auto-columns: max(200px, calc((100% - 3rem) / 1.75));
    }
  }

  .relatedProjects > div > :global(a) {
    position: relative;
  }

  .relatedProjects > div:last-child > :global(a::after) {
    content: '';
    position: absolute;
    top: 0;
    right: -1px;
    display: block;
    width: 1px;
    height: 100%;
    pointer-events: none;
  }
</style>

{#if isLoadingProject}
  <div class="loadingContainer">
    <LoadingDots />
  </div>
{:else if !project}
  <NotFound404Page title="404 – Project not found!" />
{:else}
  <div class="container">
    <section class="projectContainer">
      {#if project.media?.length}
        <MediaSlider media={project.media} />
      {/if}

      <header>
        <h1>{title}</h1>

        <div class="period">
          {#if project.creationPeriod}
            {#if !project.creationPeriod.end}Since{/if}
            {#if project.creationPeriod.start && isValidDate(project.creationPeriod.start)}
              <time
                datetime={project.creationPeriod.start
                  .toISOString()
                  .split('T')[0]}>
                {project.creationPeriod.start.toLocaleDateString(undefined, {
                  month: 'short',
                  year: 'numeric'
                })}
              </time>
            {/if}
            {#if project.creationPeriod.end && isValidDate(project.creationPeriod.end)}
              <span>&nbsp;–&nbsp;</span>
              <time
                datetime={project.creationPeriod.end
                  .toISOString()
                  .split('T')[0]}>
                {project.creationPeriod.end.toLocaleDateString(undefined, {
                  month: 'short',
                  year: 'numeric'
                })}
              </time>
            {/if}
          {:else}
            <span class="missingData">Unknown creation period</span>
          {/if}
        </div>

        {#if project.isDraft || project.confidentiality === Confidentiality.CONFIDENTIAL}
          <div class="badges">
            {#if project.isDraft}
              <DraftBadge />
            {/if}
            {#if project.confidentiality === Confidentiality.CONFIDENTIAL}
              <ConfidentialBadge />
            {/if}
          </div>
        {/if}
      </header>

      <div class="details">
        <div class="overview">
          <dl>
            <dt>Visibility:</dt>
            <dd>
              {#if project.visibility}
                {project.visibility}
              {:else}
                <span class="missingData">Unknown</span>
              {/if}
            </dd>

            {#if project.customers?.length}
              <dt>Customers:</dt>
              <dd>
                {#each project.customers as customer, customerIndex}
                  <Link
                    href={`${LIST}?customers=${encodeURIComponent(customer)}`}
                    >{customer}</Link
                  >{#if customerIndex < project.customers.length - 1}
                    {','}<br />
                  {/if}
                {/each}
              </dd>
            {/if}

            {#if $user && (project.contactPerson?.name || project.contactPerson?.url)}
              <dt>Contact person:</dt>
              <dd>
                {#if project.contactPerson.url}
                  <a href={project.contactPerson.url} target="_blank">
                    {project.contactPerson.name || project.contactPerson.url}
                  </a>
                {:else}
                  {project.contactPerson.name}
                {/if}
              </dd>
            {/if}

            {#if urlsByType}
              {#each Object.entries(urlsByType) as [type, urls]}
                <dt>{type}:</dt>
                <dd>
                  {#each urls as url, urlIndex}
                    <a href={url} target="_blank">{url}</a
                    >{#if urlIndex < urls.length - 1}
                      {','}<br />
                    {/if}
                  {/each}
                </dd>
              {/each}
            {/if}

            <dt>Project Lead:</dt>
            <dd>
              {#if project.projectLead?.length}
                {#each project.projectLead as lead, leadIndex}
                  <Link href={`${LIST}?team=${encodeURIComponent(lead)}`}
                    >{lead}</Link
                  >{#if leadIndex < project.projectLead.length - 1}
                    <br />
                  {/if}
                {/each}
              {:else}
                <span class="missingData">Unknown</span>
              {/if}
            </dd>

            <dt>Team:</dt>
            <dd>
              {#if project.team?.length}
                {#each project.team as teamMember, teamMemberIndex}
                  <Link href={`${LIST}?team=${encodeURIComponent(teamMember)}`}
                    >{teamMember}</Link
                  >{#if teamMemberIndex < project.team.length - 1}
                    <br />
                  {/if}
                {/each}
              {:else}
                <span class="missingData">Unknown</span>
              {/if}
            </dd>

            <dt class="chips">Services:</dt>
            <dd class="chips">
              {#if project.services?.length}
                <div>
                  {#each project.services as service}
                    <Chip
                      onClick={() =>
                        navigateTo(LIST, {queryParams: {services: [service]}})}>
                      {service}
                    </Chip>
                  {/each}
                </div>
              {:else}
                <span class="missingData">Unknown</span>
              {/if}
            </dd>

            <dt class="chips">Verticals / Industries:</dt>
            <dd class="chips">
              {#if project.fields?.length}
                <div>
                  {#each project.fields as field}
                    <Chip
                      onClick={() =>
                        navigateTo(LIST, {queryParams: {fields: [field]}})}>
                      {field}
                    </Chip>
                  {/each}
                </div>
              {:else}
                <span class="missingData">Unknown</span>
              {/if}
            </dd>

            <dt class="chips">Focus / Topic:</dt>
            <dd class="chips">
              {#if project.topics?.length}
                <div>
                  {#each project.topics as topic}
                    <Chip
                      onClick={() =>
                        navigateTo(LIST, {queryParams: {topics: [topic]}})}>
                      {topic}
                    </Chip>
                  {/each}
                </div>
              {:else}
                <span class="missingData">Unknown</span>
              {/if}
            </dd>

            {#if project.type === ProjectType.DEVELOPMENT}
              <dt class="chips">Use cases:</dt>
              <dd class="chips">
                {#if project.tasks?.length}
                  <div>
                    {#each project.tasks as task}
                      <Chip
                        color="green"
                        onClick={() =>
                          navigateTo(LIST, {queryParams: {tasks: [task]}})}>
                        {task}
                      </Chip>
                    {/each}
                  </div>
                {:else}
                  <span class="missingData">Unknown</span>
                {/if}
              </dd>

              <dt class="chips">Technologies:</dt>
              <dd class="chips">
                {#if project.technologies?.length}
                  <div>
                    {#each project.technologies as technology}
                      <Chip
                        color="green"
                        onClick={() =>
                          navigateTo(LIST, {
                            queryParams: {technologies: [technology]}
                          })}>
                        {technology}
                      </Chip>
                    {/each}
                  </div>
                {:else}
                  <span class="missingData">Unknown</span>
                {/if}
              </dd>
            {/if}

            {#if project.type === ProjectType.CONSULTING}
              <dt class="chips">Formats:</dt>
              <dd class="chips">
                {#if project.formats?.length}
                  <div>
                    {#each project.formats as format}
                      <Chip
                        color="yellow"
                        onClick={() =>
                          navigateTo(LIST, {queryParams: {formats: [format]}})}>
                        {format}
                      </Chip>
                    {/each}
                  </div>
                {:else}
                  <span class="missingData">Unknown</span>
                {/if}
              </dd>

              <dt class="chips">Methods:</dt>
              <dd class="chips">
                {#if project.methods?.length}
                  <div>
                    {#each project.methods as method}
                      <Chip
                        color="yellow"
                        onClick={() =>
                          navigateTo(LIST, {queryParams: {methods: [method]}})}>
                        {method}
                      </Chip>
                    {/each}
                  </div>
                {:else}
                  <span class="missingData">Unknown</span>
                {/if}
              </dd>
            {/if}

            {#if $user}
              {#if 'valueInTime' in project && project.valueInTime}
                <dt>Value in Time:</dt>
                <dd>
                  {new Intl.NumberFormat('de-DE').format(project.valueInTime)}
                </dd>
              {/if}

              {#if 'valueInPrice' in project && project.valueInPrice}
                <dt>Value in Price:</dt>
                <dd>
                  {new Intl.NumberFormat('de-DE', {
                    style: 'currency',
                    currency: 'EUR'
                  }).format(project.valueInPrice)}
                </dd>
              {/if}
            {/if}
          </dl>

          {#if project.diamond}
            <div
              class="diamondWrapper"
              on:mouseenter={() => (isDiamondHovered = true)}
              on:mouseleave={() => (isDiamondHovered = false)}>
              <Diamond
                diamond={project.diamond}
                showAxesAndLabels={isDiamondHovered} />
            </div>
          {/if}
        </div>

        <div>
          {#if description}
            {@html marked(description)}
          {:else}
            <span class="missingData">No description provided</span>
          {/if}
        </div>
      </div>
    </section>

    {#if $user}
      <div class="projectActions">
        <div class="exportWrapper">
          <Button
            variant="secondary"
            isLoading={isGeneratingSlides}
            on:click={() => exportToGoogleSlides()}
            aria-labelledby="slides-export-label">
            <span slot="icon"><InlineSVG src={slidesIcon} /></span>
            <span id="slides-export-label">Export to Google Slides</span>
          </Button>

          {#if slideUrl && !slideError}
            <a
              href={slideUrl}
              target="_blank"
              on:click={() => (slideOpened = true)}>
              Open Slides
            </a>
          {/if}

          {#if slideError}
            <small class="errorMessage">
              Export failed. Please try again.
            </small>
          {/if}
        </div>

        {#if JSZip.support.blob && project.media?.filter(media => media.type === 'image').length}
          <Button
            variant="secondary"
            isLoading={isDownloadingImages}
            on:click={downloadAllProjectImages}
            aria-labelledby="download-images-label">
            <span slot="icon"><InlineSVG src={downloadIcon} /></span>
            <span id="download-images-label">Download images</span>
          </Button>
        {/if}

        <LinkButton href={EDIT_PROJECT.replace(':id', id)}>
          <span slot="icon"><InlineSVG src={editIcon} /></span>
          Edit project
        </LinkButton>
      </div>
    {/if}

    {#if relatedProjects.length}
      <section class="relatedContent">
        <h2>Related projects</h2>

        <div class="relatedProjects">
          <div>
            {#each relatedProjects as relatedProject}
              <ProjectCard
                id={relatedProject.id}
                type={relatedProject.type}
                isDraft={relatedProject.isDraft}
                title={relatedProject.title}
                confidentiality={relatedProject.confidentiality}
                customers={relatedProject.customers}
                description={relatedProject.description}
                media={relatedProject.media}
                creationPeriod={relatedProject.creationPeriod} />
            {/each}
          </div>
        </div>
      </section>
    {/if}
  </div>
{/if}
